import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, useTheme } from '@mui/material';
import { compact, startCase } from 'lodash-es';
import { useState } from 'react';

import Chips from '../../../components/Chips';
import { CookingStationName, FoodComponentStationTitle } from '../../../libs';
import FilterButtons from '../../LeftoverMetrics/Common/FilterButtons';
import { ComponentsCheckFilters, ComponentStatus } from '../types';

interface FilterItemsProps {
  filters: ComponentsCheckFilters;
  setFilters: React.Dispatch<React.SetStateAction<ComponentsCheckFilters>>;
  getDefaultFilters: () => ComponentsCheckFilters;
  toggleFilersDrawer: (bool: boolean) => void;
}

export default function FilterItems({ filters, setFilters, getDefaultFilters, toggleFilersDrawer }: FilterItemsProps) {
  const theme = useTheme();
  const [selectedStations, setSelectecStations] = useState<CookingStationName[]>(filters.stations);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(filters.statuses);

  function onApplyFilters() {
    setFilters({
      ...filters,
      stations: selectedStations,
      statuses: selectedStatuses
    });
    toggleFilersDrawer(false);
  }
  function onClearFilters() {
    setFilters(getDefaultFilters());
    toggleFilersDrawer(false);
  }

  return (
    <Stack
      direction={'column'}
      sx={{
        justifyContent: 'space-between',
        height: '100%'
      }}
    >
      <Stack direction={'column'} spacing={3.5}>
        <FormControl fullWidth>
          <InputLabel>{'Sections'}</InputLabel>
          <Select
            label={'Sections'}
            value={selectedStations}
            multiple
            onChange={(e) => {
              const selectedStations = (e.target.value ? compact(e.target.value) : []) as (CookingStationName | 'all')[];

              if (selectedStations.includes('all')) {
                if (selectedStations.length === Object.keys(FoodComponentStationTitle).length + 1) {
                  setSelectecStations([]);
                  return;
                }
                setSelectecStations(Object.keys(FoodComponentStationTitle) as CookingStationName[]);
                return;
              }

              setSelectecStations(selectedStations as CookingStationName[]);
            }}
            renderValue={(stations) => {
              return (
                <Chips
                  values={stations}
                  onDelete={(station) => {
                    setSelectecStations(selectedStations.filter((selectedStation) => selectedStation !== station));
                  }}
                />
              );
            }}
          >
            <MenuItem key={'all'} value={'all'} sx={{ borderBottom: '1px solid', borderColor: theme.palette.neutral300 }}>
              <Checkbox
                checkedIcon={<IndeterminateCheckBoxIcon />}
                checked={selectedStations.length === Object.keys(FoodComponentStationTitle).length}
              />
              <ListItemText primary={'All Sections'} />
            </MenuItem>

            {Object.entries(FoodComponentStationTitle).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={selectedStations.includes(key as CookingStationName)} />
                <ListItemText primary={value} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>{'Status'}</InputLabel>
          <Select
            label={'Status'}
            value={selectedStatuses}
            multiple
            onChange={(e) => {
              const selectedStatuses = e.target.value ? compact(e.target.value) : [];

              if (selectedStatuses.includes('all')) {
                if (selectedStatuses.length === Object.keys(ComponentStatus).length + 1) {
                  setSelectedStatuses([]);
                  return;
                }
                setSelectedStatuses(Object.keys(ComponentStatus));
                return;
              }

              setSelectedStatuses(selectedStatuses);
            }}
            renderValue={(statuses) => {
              return (
                <Chips
                  values={statuses}
                  onDelete={(status) => {
                    setSelectedStatuses(selectedStatuses.filter((selectedStatus) => status !== selectedStatus));
                  }}
                />
              );
            }}
          >
            <MenuItem key={'all'} value={'all'} sx={{ borderBottom: '1px solid', borderColor: theme.palette.neutral300 }}>
              <Checkbox
                checkedIcon={<IndeterminateCheckBoxIcon />}
                checked={selectedStatuses.length === Object.keys(ComponentStatus).length}
              />
              <ListItemText primary={'All Statuses'} />
            </MenuItem>
            {Object.keys(ComponentStatus)?.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={selectedStatuses.includes(status)} />
                <ListItemText primary={startCase(status)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <FilterButtons
        clearButtonStyles={{
          height: '42px',
          fontSize: '15px',
          fontWeight: 500
        }}
        applyButtonStyles={{
          height: '42px',
          fontSize: '15px',
          fontWeight: 500
        }}
        onClearFilters={onClearFilters}
        onApplyFilters={onApplyFilters}
      />
    </Stack>
  );
}
